import React, {useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { Overlay } from '../UI/Modal/Modal';
import AuthInput from '../UI/Input/AuthInputs';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { ButtonSubmitGreen } from "../UI/Buttons/Buttons";
import { setFetching } from '../../redux/states';
import { checkAuthenticatedUser } from '../../utils/client-functions';
import { useErrorSetter, useSuccessSetter } from '../../hooks/useUiMessages';
import axios from 'axios';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import { requestNotificationPermission, subscribeUserToPush, hasClientPushSubscription } from '../../utils/pwa-utils';
import styled, { keyframes } from 'styled-components';
import { useTheme } from "../UI/Theme/ThemeContext";
import ProfileEmailPreferences from '../Email/ProfileEmailPreferences';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(62, 143, 147, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(62, 143, 147, 0); }
  100% { box-shadow: 0 0 0 0 rgba(62, 143, 147, 0); }
`;

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const ProfileCard = styled.div`
  background: ${props => props.$isDark 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(16px);
  border: 1px solid ${props => props.$isDark 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(62, 143, 147, 0.2)'};
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  color: ${props => props.$isDark ? '#fff' : '#0C1E28'};
  position: relative;
  overflow: hidden;

  @media (max-width: 600px) {
    padding: 24px 16px;
    border-radius: 16px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3E8F93, #0d3d40, #3E8F93);
  }

  /* Input overrides for theme consistency */
  input, select, textarea, .react-phone-input-2 .form-control {
    background: ${props => props.$isDark ? 'rgba(255, 255, 255, 0.08) !important' : 'rgba(62, 143, 147, 0.05) !important'};
    color: ${props => props.$isDark ? '#fff !important' : '#0C1E28 !important'};
    border: 1px solid ${props => props.$isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(62, 143, 147, 0.2)'} !important;
    border-radius: 12px !important;
  }

  /* MUI specific overrides */
  .MuiOutlinedInput-root {
    transition: all 0.3s ease;
    background: ${props => props.$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(62, 143, 147, 0.05)'};
    
    &:hover, &.Mui-focused {
      background: ${props => props.$isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(62, 143, 147, 0.1)'};
      box-shadow: 0 0 0 2px rgba(62, 143, 147, 0.2);
    }
  }

  .MuiInputBase-input {
    color: ${props => props.$isDark ? '#fff !important' : '#0C1E28 !important'};
  }

  .MuiInputLabel-root {
    color: ${props => props.$isDark ? 'rgba(255, 255, 255, 0.6) !important' : '#3E8F93 !important'};
  }

  .Mui-disabled {
    -webkit-text-fill-color: ${props => props.$isDark ? 'rgba(255, 255, 255, 0.4) !important' : 'rgba(0, 0, 0, 0.38) !important'};
    opacity: 0.8;
  }
`;

const ProfileHeader = styled.h2`
  font-size: 1.8rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 40px;
  letter-spacing: 2px;
  background: linear-gradient(90deg, #3E8F93, #0d3d40, #3E8F93);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-transform: uppercase;
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 40px;
`;

const ImageWrapper = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  padding: 4px;
  background: linear-gradient(45deg, #3E8F93, transparent, #3E8F93);
  animation: ${pulseGlow} 3s infinite;
  margin-bottom: 20px;
  position: relative;
`;

const ImageInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: ${props => props.$isDark ? '#1a1a1a' : '#f0f0f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.$isDark ? '#333' : '#fff'};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UploadLabel = styled.label`
  padding: 8px 20px;
  background: rgba(62, 143, 147, 0.1);
  border: 1px solid #3E8F93;
  color: #3E8F93;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #3E8F93;
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(62, 143, 147, 0.3);
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.h5`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #3E8F93;
  margin: 24px 0 16px;
  text-transform: uppercase;
  letter-spacing: 1px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${props => props.$isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
  }
`;

const NotificationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: ${props => props.$isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  border-radius: 16px;
  margin-bottom: 30px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px;
  }
`;

const SaveButton = styled(ButtonSubmitGreen)`
  && {
    background: linear-gradient(90deg, #3E8F93, #0d3d40);
    border: none;
    height: 54px;
    border-radius: 16px;
    font-weight: 700;
    letter-spacing: 1.5px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 15px rgba(62, 143, 147, 0.3);
    width: 100%;
    max-width: 300px;
    color: white;
    cursor: pointer;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(62, 143, 147, 0.5);
      filter: brightness(1.1);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;

const sexOptions = [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' },
        { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const Profile = () => {
    const { themeName } = useTheme();
    const isDark = themeName === 'dark';
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const [isResumeSubscribed, setIsResumeSubscribed] = useState(false)
    const [resumeDuration, setResumeDuration] = useState("Per Month")
    const [resumeSubExpiration, setResumeSubExpiration] = useState("")
    const [isExamSubscribed, setIsExamSubscribed] = useState(false)
    const [examDuration, setExamDuration] = useState("Free Plan")
    const [examSubExpiration, setExamSubExpiration] = useState("")
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [email, setEmail] = useState("")
    const [dob, setDob] = useState("")
    const [sex, setSex] = useState("")
    const [mobile, setMobile] = useState("")
    const [stateRegion, setStateRegion] = useState("")
    const [country, setCountry] = useState("")
    const [profileImg, setProfileImg] = useState("");
    const [uploadingImg, setUploadingImg] = useState(false);
    // Push notifications toggle state
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushLoading, setPushLoading] = useState(false);
    //page where cancel icon would lead to
    const prevPath = sessionStorage.getItem("prevPath")
    const resumeSub = isResumeSubscribed ? resumeDuration : "Unsubscribed"; 
    const resumeSubExp = isResumeSubscribed && resumeSubExpiration ? resumeSubExpiration : "N/A";
    const examSub = isExamSubscribed ? examDuration : "Unsubscribed";
    const examSubExp = isExamSubscribed && examSubExpiration ? examSubExpiration : "N/A";


    const errorSetter = useErrorSetter();
    const successSetter = useSuccessSetter();

    useEffect(() => {
        const isAuth = localStorage?.getItem("token");
        const populateProfile = async () => {
            try {
                //must await
                await checkAuthenticatedUser()
            } catch (error) {
                sessionStorage.setItem("prevPath", "/user/dashboard/profile")
                return navigate("/popin");      
            }
            try {
                dispatch(setFetching(true))
                const response = await axios.get("/dashboard/get-profile", {
                    headers: {
                      "x-access-token": isAuth,
                    },
                });
                const profileData = response.data.profile
                setProfileImg(profileData.profileImg);
                setIsResumeSubscribed(profileData.subscriptions.resume.subscribed)
                setResumeDuration(profileData.subscriptions.resume?.duration)
                setResumeSubExpiration(profileData.subscriptions.resume?.endDate)
                setIsExamSubscribed(profileData.subscriptions?.exam?.subscribed)
                setExamDuration(profileData.subscriptions?.exam?.duration)
                setExamSubExpiration(profileData.subscriptions?.exam?.endDate)
                setFirstName(profileData.firstName)
                setLastName(profileData.lastName)
                setEmail(profileData.email)
                setDob(profileData?.dob)
                setSex(profileData?.sex || "")
                setMobile(profileData.mobile)
                setStateRegion(profileData.stateRegion)
                setCountry(profileData.country)

                // Fetch server-side push status
                try {
                    const statusResp = await axios.get('/user/push/status', { headers: { 'x-access-token': isAuth } });
                    setPushEnabled(Boolean(statusResp?.data?.enabled));
                } catch(_) {
                    // best-effort: fall back to client subscription presence
                    try {
                        const hasSub = await hasClientPushSubscription();
                        setPushEnabled(hasSub);
                    } catch(_) {}
                }
                
                dispatch(setFetching(false))
            } catch (error) {
                if(error?.response?.status === 401) {
                    sessionStorage.setItem("prevPath", "/user/dashboard/profile")
                    dispatch(setFetching(false))
                    return navigate("/popin"); 
                }
                dispatch(setFetching(false))
                errorSetter("Something went wrong, reload page")
            }

        }
        populateProfile()
    }, [navigate, dispatch, errorSetter])
   

    const handleProfileImgChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return errorSetter("Please select an image.");
        if (!["image/jpeg", "image/png"].includes(file.type)) {
            return errorSetter("Only JPG and PNG images are allowed.");
        }
        if (file.size > 2 * 1024 * 1024) {
            return errorSetter("Image size must be under 2MB.");
        }
        if (file.size > 2 * 1024 * 1024) {
            return errorSetter("Image size must be under 2MB.");
        }
        setUploadingImg(true);
        const formData = new FormData();
        formData.append("file", file);
        try {
            const isAuth = localStorage?.getItem("token");
            const response = await axios.post("/dashboard/upload-profile-img", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    "x-access-token": isAuth
                }
            });
            setProfileImg(response.data.url);
            setUploadingImg(false);
            successSetter("Profile image changed!");
        } catch (error) {
            setUploadingImg(false);
            errorSetter("Error uploading image.");
        }
    };

    const handlePushToggle = async (event) => {
        const next = event.target.checked;
        const isAuth = localStorage?.getItem('token');
        if (!isAuth) {
            sessionStorage.setItem('prevPath', '/user/dashboard/profile');
            return navigate('/popin');
        }
        setPushLoading(true);
        try {
            if (next) {
                // Turn ON: ensure permission and subscribe + bind to server
                let perm = Notification?.permission || 'default';
                if (perm === 'default') {
                    perm = await requestNotificationPermission();
                }
                if (perm !== 'granted') {
                    setPushLoading(false);
                    return errorSetter('Notifications are blocked. Enable them in your browser/app settings.');
                }
                const ok = await subscribeUserToPush();
                if (!ok) {
                    setPushLoading(false);
                    return errorSetter('Failed to enable push notifications.');
                }
                setPushEnabled(true);
                successSetter('Push notifications enabled.');
            } else {
                // Turn OFF: disable on server only (keep client subscription so prompt can still show)
                await axios.post('/user/push/unsubscribe', {}, { headers: { 'x-access-token': isAuth } });
                setPushEnabled(false);
                successSetter('Push notifications disabled for this account.');
            }
        } catch (e) {
            errorSetter('Operation failed. Please try again.');
        } finally {
            setPushLoading(false);
        }
    };

    const handleProfileSave = async () => {
        const isAuth = localStorage?.getItem("token");
        try {
            //must await
            await checkAuthenticatedUser()
        } catch (error) {
            sessionStorage.setItem("prevPath", "/user/dashboard/profile")
            return navigate("/popin");      
        }
        if (dob.length < 10 || mobile.length < 5 || stateRegion === "" || country === "" ) {
            return errorSetter("You have not made valid changes")
        } else {
            dispatch(setFetching(true))
            try {
                const body = {
                    dob: dob,
                    sex: sex,
                    mobile: mobile,
                    stateRegion: stateRegion,
                    country: country
                }
                const response = await axios.post("/dashboard/edit-profile", body, {
                    headers: {
                        "x-access-token": isAuth,
                    },
                })

                if (response.status === 201) {
                    const prevPath = sessionStorage?.getItem("prevPath")
                    navigate(prevPath);
                    dispatch(setFetching(false))
                    return
                }
                dispatch(setFetching(false))
                errorSetter("Something went wrong, try again")

            } catch (error) {
                dispatch(setFetching(false))
                if(error?.response?.status === 401) {
                    sessionStorage.setItem("prevPath", "/dashboard/get-profile")
                    dispatch(setFetching(false))
                    return navigate("/popin"); 
                }
                dispatch(setFetching(false))
                errorSetter("Something went wrong, try again")
            }

        }
    }


    const handleDobChange = (event) => {
        setDob(event.target.value)
    };
    const handleSexChange = (event) => {
        setSex(event.target.value)
    };
    const handleMobileChange = (event) => {
        setMobile(event)
    };
    const handleStateRegionChange = (event) => {
        setStateRegion(event)
    };
    const handleCountryChange = (event) => {
        setCountry(event)
    };
    

    return (
        <Overlay prevPath={prevPath}>
            <ProfileContainer>
                <ProfileCard $isDark={isDark}>
                    <ProfileHeader>Edit Profile</ProfileHeader>

                    <ImageSection>
                        <ImageWrapper>
                            <ImageInner $isDark={isDark}>
                                {profileImg ? (
                                    <img src={profileImg} alt="Profile" />
                                ) : (
                                    <AccountCircleIcon sx={{ color: "#3E8F93", fontSize: "4rem" }} />
                                )}
                            </ImageInner>
                        </ImageWrapper>
                        <UploadLabel className={uploadingImg ? 'disabled' : ''}>
                            <input
                                type="file"
                                accept=".jpg,.jpeg,.png"
                                style={{ display: "none" }}
                                onChange={handleProfileImgChange}
                                disabled={uploadingImg}
                            />
                            {uploadingImg ? (
                                <>
                                    <CircularProgress size={16} sx={{ color: '#3E8F93' }} />
                                    <span>Uploading...</span>
                                </>
                            ) : (
                                "Change Profile Image"
                            )}
                        </UploadLabel>
                        <span style={{ fontSize: '0.65rem', color: isDark ? 'rgba(255,255,255,0.4)' : '#666', marginTop: '8px' }}>
                            2MB max (JPG/PNG)
                        </span>
                    </ImageSection>

                    <SectionTitle $isDark={isDark}>Personal Info</SectionTitle>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"firstName"}
                                value={firstName}
                                label="First Name"
                                inputType="text"
                                inputGridSm={12}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"lastName"}
                                value={lastName}
                                label="Last Name"
                                inputType="text"
                                inputGridSm={12}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"email"}
                                value={email}
                                label="Your Email"
                                inputType="email"
                                inputGridSm={12}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"dob"}
                                value={dob}
                                label="Date of Birth"
                                inputType="date"
                                inputGridSm={12}
                                required={true}
                                onChange={handleDobChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"sex"}
                                value={sex}
                                label="Choose Sex"
                                inputType="select2"
                                inputGridSm={12}
                                required={true}
                                list={sexOptions}
                                onChange={handleSexChange}
                                darkMode={isDark}
                            />
                        </Grid>
                    </Grid>

                    <SectionTitle $isDark={isDark}>Contact & Location</SectionTitle>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"mobile"}
                                value={mobile}
                                label="Mobile"
                                inputType="mobile"
                                inputGridSm={12}
                                required={true}
                                onChange={handleMobileChange}
                                darkMode={isDark}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"country"}
                                name={country}
                                value={country}
                                inputType="country-select"
                                inputGridSm={12}
                                required={true}
                                onChange={handleCountryChange}
                            />
                            <div style={{ fontSize: '0.65rem', color: '#EE7B1C', marginTop: '4px' }}>
                                Used to connect you to local job opportunities
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                country={country}
                                id={"stateRegion"}
                                name={stateRegion}
                                value={stateRegion}
                                inputType="state-select"
                                inputGridSm={12}
                                required={true}
                                onChange={handleStateRegionChange}
                            />
                        </Grid>
                    </Grid>

                    <SectionTitle $isDark={isDark}>Current Subscriptions</SectionTitle>
                    <Grid container spacing={2} style={{ marginBottom: '30px' }}>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"resumeSub"}
                                value={`${resumeSub}`}
                                label="Resume Plan"
                                inputType="text"
                                disabled={true}
                                inputGridSm={12}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"resumeSubExp"}
                                value={`${resumeSubExp.slice(0, 10)}`}
                                label="Expiration"
                                inputType="text"
                                disabled={true}
                                inputGridSm={12}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"examSub"}
                                value={`${examSub}`}
                                label="Exam Plan"
                                inputType="text"
                                disabled={true}
                                inputGridSm={12}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AuthInput
                                id={"examSubExp"}
                                value={`${examSubExp.slice(0, 10)}`}
                                label="Expiration"
                                inputType="text"
                                disabled={true}
                                inputGridSm={12}
                            />
                        </Grid>
                    </Grid>

                    <SectionTitle $isDark={isDark}>Notifications</SectionTitle>
                    <NotificationRow $isDark={isDark}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '1rem', color: isDark ? '#fff' : '#0C1E28' }}>
                                Push Notifications
                            </div>
                            <div style={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.5)' : '#666', marginTop: '4px', maxWidth: '300px' }}>
                                Receive real-time updates on your applications and AI assistant replies.
                            </div>
                        </div>
                        <FormControlLabel
                            control={
                                pushLoading ? (
                                    <span><CircularProgress size={20} sx={{ color: '#3E8F93' }} /> &nbsp;</span>
                                ) : (
                                    <Switch
                                        checked={pushEnabled}
                                        onChange={handlePushToggle}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#3E8F93',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#3E8F93',
                                            },
                                            '& .MuiSwitch-track': {
                                                backgroundColor: 'rgba(62,143,147,0.3)',
                                            }
                                        }}
                                    />
                                )
                            }
                            label={pushEnabled ? <span style={{ fontWeight: 600, color: '#3E8F93' }}>ON</span> : <span style={{ fontWeight: 600, color: '#999' }}>OFF</span>}
                        />
                    </NotificationRow>

                    <SectionTitle $isDark={isDark}>Email Notifications</SectionTitle>
                    <ProfileEmailPreferences isDark={isDark} />

                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
                        <SaveButton
                            type="button"
                            onClick={handleProfileSave}
                        >
                            SAVE & CLOSE
                        </SaveButton>
                    </div>
                </ProfileCard>
            </ProfileContainer>
        </Overlay>
    );
};

export default Profile;
